import { ColumnProps } from "antd/lib/table";
import { renderMasterIndex } from "helpers/table";
import { FileTemplate } from "models/FileTemplate";
import { FileTemplateInput } from "models/FileTemplateInput";
import React from "react";
import { LayoutCell, LayoutHeader, OneLineText } from "react3l-ui-library";
import nameof from "ts-nameof.macro";
import { useTranslation } from "react-i18next";

enum FileTemplateInputEnum {
  UPDATE_ALL,
  UPDATE_FILE_TEMPLATE_INPUTS,
  UPDATE_VISIBLE,
  UPDATE_CURRENT_FILE_TEMPLATE,
}

interface FileTemplateInputState {
  fileTemplateInputs?: FileTemplateInput[];
  visible?: boolean;
  fileTemplate?: FileTemplate;
}

interface FileTemplateInputAction {
  type: FileTemplateInputEnum;
  data: FileTemplateInputState;
}

function fileTemplateInputReducer(
  state: FileTemplateInputState,
  action: FileTemplateInputAction
): FileTemplateInputState {
  switch (action.type) {
    case FileTemplateInputEnum.UPDATE_ALL:
      return {
        ...state,
        fileTemplateInputs: [...action.data.fileTemplateInputs],
        visible: action.data.visible,
        fileTemplate: { ...action.data.fileTemplate },
      };
    case FileTemplateInputEnum.UPDATE_FILE_TEMPLATE_INPUTS:
      return {
        ...state,
        fileTemplateInputs: [...action.data.fileTemplateInputs],
      };
    case FileTemplateInputEnum.UPDATE_VISIBLE:
      return {
        ...state,
        visible: action.data.visible,
      };
  }
}

export function useFileTemplateInputModal() {
  const [translate] = useTranslation();

  const [
    { fileTemplateInputs: fileTemplateInputsInternal, visible },
    dispatchValue,
  ] = React.useReducer(fileTemplateInputReducer, {
    fileTemplateInputs: [],
    visible: false,
  });

  const handleOpenModal = React.useCallback((fileTemplate: FileTemplate) => {
    dispatchValue({
      type: FileTemplateInputEnum.UPDATE_ALL,
      data: {
        fileTemplateInputs:
          fileTemplate && fileTemplate.fileTemplateInputs
            ? fileTemplate.fileTemplateInputs
            : [],
        visible: true,
        fileTemplate: fileTemplate,
      },
    });
  }, []);

  const handleCloseModal = React.useCallback(() => {
    dispatchValue({
      type: FileTemplateInputEnum.UPDATE_VISIBLE,
      data: {
        visible: false,
      },
    });
  }, []);

  const columns: ColumnProps<FileTemplateInput>[] = React.useMemo(() => {
    return [
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("general.columns.index")}
          />
        ),
        key: "index",
        width: 60,
        align: "center",
        fixed: true,
        render: renderMasterIndex<FileTemplateInput>(),
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplateInputs.fieldName")}
          />
        ),
        key: nameof("fieldName"),
        width: 150,
        dataIndex: nameof("fieldName"),
        render(...params: [string, FileTemplateInput, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <OneLineText value={params[0]} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplateInputs.label")}
          />
        ),
        key: nameof("label"),
        width: 150,
        dataIndex: nameof("label"),
        render(...params: [string, FileTemplateInput, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <OneLineText value={params[0]} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplateInputs.fieldValue")}
          />
        ),
        key: nameof("fieldValue"),
        dataIndex: nameof("fieldValue"),
        width: 150,

        render(...params: [string, FileTemplateInput, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <OneLineText value={params[0]} countCharacters={50} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplateInputs.order")}
            className="p-1"
          />
        ),
        width: 100,
        key: nameof("order"),
        dataIndex: nameof("order"),
        render(...params: [number, FileTemplateInput, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <OneLineText value={params[0]?.toString()} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplateInputs.fileTemplateInputTypeId")}
          />
        ),
        width: 100,
        key: nameof("fileTemplateInputTypeId"),
        dataIndex: nameof("fileTemplateInputTypeId"),
        render(...params: [number, FileTemplateInput, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <OneLineText value={params[0]?.toString()} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplateInputs.columnNumber")}
          />
        ),
        width: 100,
        key: nameof("columnNumber"),
        dataIndex: nameof("columnNumber"),
        render(...params: [number, FileTemplateInput, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <OneLineText value={params[0]?.toString()} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplateInputs.dataSource")}
          />
        ),
        key: nameof("dataSource"),
        width: 150,
        dataIndex: nameof("dataSource"),
        render(...params: [string, FileTemplateInput, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <OneLineText value={params[0]} countCharacters={35} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplateInputs.isDisplayOrderNumber")}
          />
        ),
        width: 60,
        key: nameof("isDisplayOrderNumber"),
        dataIndex: nameof("isDisplayOrderNumber"),
        render(...params: [number, FileTemplateInput, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <OneLineText value={params[0] === 0 ? "No" : "Yes"} />
            </LayoutCell>
          );
        },
      },
    ];
  }, [translate]);

  return {
    handleOpenModal,
    handleCloseModal,
    fileTemplateInputColumns: columns,
    fileTemplateInputsInternal,
    visible,
  };
}
